/**
 * Lifting an AFF line — a tail or a drawn track — to its aircraft's altitude in
 * 3D (#820 step 8).
 *
 * One place for the height maths, so tails and tracks cannot drift apart. The
 * line carries an `elevation` array property: metres above sea level, one per
 * point of its flat [lon, lat] geometry.
 *
 * Verified in the browser 2026-09-13, not taken from the docs alone:
 *  - line-z-offset read through line-progress varies per point along one line,
 *    and the array survives setData (checked against a flat control line);
 *  - 'sea' with line-elevation-ground-scale 1 and the REAL altitude met the 3D
 *    model body exactly — identical to ground-scale 0 with altitude times
 *    exaggeration. mapbox applies the exaggeration, so the data never needs to
 *    know it, and no terrain is looked up per point.
 *
 * ground-scale is set explicitly: its default is undocumented. The source needs
 * `lineMetrics: true` for line-progress to exist.
 */

/**
 * Each point's height, read along the line. line-progress runs 0 to 1 from the
 * first point to the last; scaled by the array's last index, at-interpolated
 * reads the height there and blends between neighbours.
 */
export const LINE_Z_OFFSET = [
  'at-interpolated',
  ['*', ['line-progress'], ['-', ['length', ['get', 'elevation']], 1]],
  ['get', 'elevation'],
] as const;

/** A line layout, lifted to the altitude in its `elevation` property. */
export function liftedLineLayout<T extends object>(layout: T) {
  return {
    ...layout,
    'line-elevation-reference': 'sea',
    'line-elevation-ground-scale': 1,
    'line-z-offset': LINE_Z_OFFSET,
  } as const;
}

/**
 * The heights re-read at even distance steps along the line (#820).
 *
 * LINE_Z_OFFSET reads index line-progress x (n - 1). line-progress counts
 * distance, the index counts points, so the two only agree when the points are
 * evenly spaced. A real track is not: it bunches up while parked and spreads
 * out in flight, and its heights slid along it (C-FIDA's 840m peak was drawn at
 * 559m). Returning n heights sampled at even distances makes index and distance
 * agree, so the expression reads the right height everywhere.
 *
 * Distance is measured in Web Mercator, the space line-progress is counted in,
 * not along the great circle. A line with no length keeps its heights.
 */
export function evenHeights(coordinates: ReadonlyArray<readonly [number, number]>, heights: readonly number[]): number[] {
  const n = coordinates.length;
  if (n < 2) return [...heights];

  const mercator = ([lon, lat]: readonly [number, number]): [number, number] => {
    const φ = (lat * Math.PI) / 180;
    return [(lon * Math.PI) / 180, Math.log(Math.tan(Math.PI / 4 + φ / 2))];
  };

  const cumulative = [0];
  let previous = mercator(coordinates[0]);
  for (let i = 1; i < n; i++) {
    const current = mercator(coordinates[i]);
    cumulative.push(cumulative[i - 1] + Math.hypot(current[0] - previous[0], current[1] - previous[1]));
    previous = current;
  }

  const total = cumulative[n - 1];
  if (total === 0) return [...heights];

  const resampled: number[] = [];
  let segment = 0;
  for (let k = 0; k < n; k++) {
    const target = (total * k) / (n - 1);
    while (segment < n - 2 && cumulative[segment + 1] < target) segment++;

    const start = cumulative[segment];
    const length = cumulative[segment + 1] - start;
    const t = length === 0 ? 0 : Math.min(1, Math.max(0, (target - start) / length));
    resampled.push(heights[segment] + (heights[segment + 1] - heights[segment]) * t);
  }
  return resampled;
}

/**
 * Whether every point has a real height. A single null or missing value means
 * the line is drawn flat instead: lifting the rest would draw a height that is
 * part guess, and at-interpolated over a null is an expression error.
 */
export const hasCompleteHeights = (elevation: unknown): elevation is number[] =>
  Array.isArray(elevation) &&
  elevation.length > 0 &&
  elevation.every((h) => typeof h === 'number' && Number.isFinite(h));
